import { createSelector, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BaseAsyncValue } from '@ordersnproducts/modules/shared/models/async-value';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise } from 'rxjs/operators';
import { RecipeFormValues } from '../../../retail-specification/models/form-values';
import { SpecFieldNames } from '../../../specification/constants/specification-field-names.enum';
import { getRmsRecipeSpecifications } from '../../selectors';
import { getOpenPlatformContractData } from '../../selectors/contracts.selectors';
import { ContractOP, COUNTRY_CODE } from '@cemex-core/cmx-api';
import { getCurrentLanguageWithLocaleInfo } from '@ordersnproducts/utils/utils';
import { getActiveCountryCode } from '@ordersnproducts/modules/order-builder/reducers';
import * as moment from 'moment';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { ContractsExpirationService } from '@ordersnproducts/modules/shared/services/contracts-expiration.service';

const contractsAndRecipe = createSelector(
  getOpenPlatformContractData,
  getRmsRecipeSpecifications,
  (contracts, recipe) => {
    return {
      contracts,
      recipe,
    };
  },
);

export class ContractOptionsLoaderOpenPlatformProvider extends BaseAsyncValue<SelectOptionI[]> {
  private translate: TranslateService;
  private experienceService: ExperiencesService;
  private featureToggle: FeatureToggleService;
  private contractsExpirationService: ContractsExpirationService;

  private buffer = {
    lastMasterContract: null,
    lastMasterProduct: null,
  };

  getValue(): Observable<SelectOptionI[]> {
    if (!this.experienceService) {
      this.experienceService = this.injector.get(ExperiencesService);
    }
    if (!this.featureToggle) {
      this.featureToggle = this.injector.get(FeatureToggleService);
    }
    if (!this.contractsExpirationService) {
      this.contractsExpirationService = this.injector.get(ContractsExpirationService);
    }

    return combineLatest([
      this.store.pipe(
        select(contractsAndRecipe),
        filter(({ contracts }) => !!contracts.availableContract && Object.keys(contracts.availableContract).length > 0),
        filter(({ recipe }) => recipe.length > 0),
        pairwise(),
        filter(([prev, current]) => {
          for (let i = 0; i < current.recipe.length; i++) {
            if (
              current &&
              prev &&
              current.recipe[i] &&
              prev.recipe[i] &&
              current.recipe[i].values &&
              prev.recipe[i].values &&
              current.recipe[i].values[SpecFieldNames.PRODUCT_SELECTION] &&
              prev.recipe[i].values &&
              current.recipe[i].values[SpecFieldNames.PRODUCT_SELECTION] !==
                prev.recipe[i].values[SpecFieldNames.PRODUCT_SELECTION]
            ) {
              return true;
            } else if (
              current &&
              current.recipe[i] &&
              current.recipe[i].values &&
              current.recipe[i].values[SpecFieldNames.PRODUCT_SELECTION] &&
              (!prev ||
                !current.recipe[i] ||
                !prev.recipe[i].values ||
                !prev.recipe[i].values[SpecFieldNames.PRODUCT_SELECTION])
            ) {
              return true;
            }
          }
          const masterProductChanged =
            this.buffer.lastMasterProduct !== current.recipe[0].values[SpecFieldNames.PRODUCT_SELECTION];
          if (masterProductChanged) {
            this.buffer.lastMasterProduct = current.recipe[0].values[SpecFieldNames.PRODUCT_SELECTION];
          }
          const masterContractChanged =
            this.buffer.lastMasterContract !== current.recipe[0].values[SpecFieldNames.CONTRACT_SELECT];
          if (masterContractChanged) {
            this.buffer.lastMasterContract = current.recipe[0].values[SpecFieldNames.CONTRACT_SELECT];
          }
          return current.recipe.length !== prev.recipe.length || masterProductChanged || masterContractChanged;
        }),
        map(([, current]) => current),
        map(({ contracts, recipe }) => {
          return {
            contracts,
            recipe,
          };
        }),
        distinctUntilChanged(),
      ),
      this.store.select(getCurrentLanguageWithLocaleInfo).pipe(
        filter((language) => language != null),
        map((language) => {
          return { formatDate: language.formatDate };
        }),
      ),
      this.contractsExpirationService.maxDateTimeMoment$.pipe(
        map((maxDateTimeMoment) => ({
          maxDateTimeMoment,
        })),
      ),
      this.store.select(getActiveCountryCode).pipe(
        map((countryCode) => ({
          countryCode,
        })),
      ),
      this.experienceService.commandDcoEnabled$().pipe(
        map((commandDcoEnabled) => ({
          commandDcoEnabled,
        })),
      ),
    ]).pipe(
      map(([{ contracts, recipe }, { formatDate }, { maxDateTimeMoment }, { countryCode }, { commandDcoEnabled }]) => {
        if (!this.translate) {
          this.translate = this.injector.get(TranslateService);
        }

        formatDate = formatDate || 'MM/DD/YYYY';

        if (this.isAttachedToMasterItem(recipe)) {
          const productCode = this.form.get(SpecFieldNames.PRODUCT_SELECTION).value;
          const availableContracts = contracts.availableContract[productCode];
          if (availableContracts) {
            this.handlerDefaultContractValue(availableContracts);
            return this.availableContractsMap(
              availableContracts,
              maxDateTimeMoment,
              formatDate,
              countryCode,
              commandDcoEnabled,
            );
          }
          return [];
        } else {
          // Lock contract & date selection
          const masterContract = recipe[0].values[SpecFieldNames.CONTRACT_SELECT];
          this.form.get(SpecFieldNames.CONTRACT_SELECT).setValue(masterContract);
          this.form.get(SpecFieldNames.CONTRACT_SELECT).disable();

          const masterDate = recipe[0].values[SpecFieldNames.DATE];
          this.form.get(SpecFieldNames.DATE) && this.form.get(SpecFieldNames.DATE).setValue(masterDate);
          this.form.get(SpecFieldNames.DATE) && this.form.get(SpecFieldNames.DATE).disable();

          // Return contracts from master item
          const productCode = recipe[0].values[SpecFieldNames.PRODUCT_SELECTION];
          const availableContracts = contracts.availableContract[productCode];
          if (availableContracts) {
            return this.availableContractsMap(
              availableContracts.filter((salesDocument: ContractOP) => {
                return salesDocument.contractCode.replace(/^0+/, '') === masterContract;
              }),
              maxDateTimeMoment,
              formatDate,
              countryCode,
              commandDcoEnabled,
            );
          }
          return [];
        }
      }),
    );
  }
  /**
   * Method to map available contracts to SelectOptionI[]
   * @param availableContracts
   * @param lastValidDate
   * @param formatDate
   * @param countryCode
   * @returns [{id, name},...,{}]
   */
  private availableContractsMap(
    availableContracts: ContractOP[],
    lastValidDate: moment.Moment = null,
    formatDate: string,
    countryCode: string,
    improvedDcoEnabled: boolean,
  ) {
    return availableContracts.map((salesDocument: ContractOP) => {
      return {
        id: salesDocument.contractCode.replace(/^0+/, ''),
        name: `${this.translate.instant('views.specifications.contract')} ${salesDocument.contractCode}`,
        ...(countryCode === COUNTRY_CODE.USA &&
        improvedDcoEnabled &&
        lastValidDate &&
        moment(salesDocument.validOnDateTo).isSameOrBefore(lastValidDate)
          ? {
              secondaryText: this.translate.instant('views.multiple_orders.template.contract.placeholder.expire', {
                expiration: moment(salesDocument.validOnDateTo).format(formatDate),
              }),
            }
          : {}),
      };
    });
  }
  /**
   * Defines if current provider is attached to the master item of the recipe (Frist in index position)
   * @param recipe
   * @returns
   */
  private isAttachedToMasterItem(recipe: RecipeFormValues[]) {
    return this.mapItem.id === recipe[0].id;
  }

  /**
   * Set contract value if on contracts are only one value
   * @param contracts ContractOP[]
   */
  private handlerDefaultContractValue(contracts: ContractOP[]) {
    if (contracts.length !== 1) {
      return;
    }
    const defaultContractCode = contracts[0].contractCode.replace(/^0+/, '');
    this.form.get(SpecFieldNames.CONTRACT_SELECT).setValue(defaultContractCode);
  }
}
